import { CompanyContext } from "contexts/CompanyContext";
import { AIContext } from "contexts/AIContext";
import { useContext } from "react";
import { aiImageStyles } from "views/company_settings/components/AIImages";
import { aiJsonTextToArray } from "utils/aiListTextToArray";
import { getImageAICaptionURL } from "utils/getImageAICaption";

const useRandomContexts = () => {

    const { getByQueryChat, aiModels } = useContext(AIContext);
    const { company } = useContext(CompanyContext);

    const generateRandomContexts = async (quantity = 8, isChargeable = false) => {

        const response = await getByQueryChat(`
SEED: ${new Date().getTime()}
    
Generate ${Math.ceil(quantity * 3)} different topics for social media posts.

EXAMPLES:
Example 1: Create a post about rainy days for social media managers including a CTA to the website
Example 2: Create a post about sunny days for influencers including a CTA to like and comment 

WRITING INSTRUCTIONS:
- Include important upcoming festive events in the last topics, considering today is ${new Date().toUTCString()}
- All topics must start with "Create a post about"
- Add communication tone at the end, when needed, like "using a Y tone"
- No video creation
- Topics must be 20 words long or less
- Return a valid JSON array with the topics
`, 
        false, 
        undefined, 
        undefined, 
        undefined, 
        undefined, 
        undefined, 
        undefined, 
        {
          "name": "social_media_topics",
          "schema": {
            "type": "object",
            "properties": {
              "topics": {
                "type": "array",
                "description": "Array of social media post topics",
                "items": {
                  "type": "string",
                  "description": "A social media post topic starting with 'Create a post about' and ending with a communication tone"
                }
              }
            },
            "required": ["topics"],
            "additionalProperties": false
          },
          "strict": true
        }
      );

        if (response) {
            const topics = aiJsonTextToArray(response);
            console.log(topics);
            let suggestionsArray = topics || [];
            suggestionsArray.reverse();
            suggestionsArray.length = Math.min(quantity, suggestionsArray.length);
            return suggestionsArray;
        }

        return [];
    }

    const generateRandomAIImagePrompts = async (quantity = 4, isChargeable = false, customTopic = null) => {

        const response = await getByQueryChat(`
Generate ${Math.ceil(quantity * 4)} photo advertisement ideas for ${company?.name}${customTopic ? ` related to "${customTopic}"` : ''}.

WRITING INSTRUCTIONS:
- Be very detailed and specific
- Include camera angles, lighting, and other details
- Do not include the company name
- Do not use the COMPANY name
- Do not include the COMPANY URL
- Do not include the COMPANY logo
- No captions
- Do not explain the meaning of the photo
${customTopic ? `- Focus on the topic: "${customTopic}" but make it creative and engaging` : ''}
- Return a valid JSON array with the photo ideas
`, 
        false, 
        undefined, 
        undefined, 
        undefined, 
        undefined, 
        undefined, 
        undefined, 
        {
          "name": "photo_advertisement_ideas",
          "schema": {
            "type": "object",
            "properties": {
              "photo_ideas": {
                "type": "array",
                "description": "Array of photo advertisement ideas",
                "items": {
                  "type": "string",
                  "description": "A creative photo advertisement idea without company name, URL, or logo"
                }
              }
            },
            "required": ["photo_ideas"],
            "additionalProperties": false
          },
          "strict": true
        }
      );

        if (response) {
            const photo_ideas = aiJsonTextToArray(response);
            let suggestionsArray = photo_ideas || [];
            suggestionsArray.reverse();
            suggestionsArray.length = Math.min(quantity, suggestionsArray.length);
            return suggestionsArray;
        }

        return [];
    }


    const generateRandomAIVideoPrompts = async (quantity = 4, isChargeable = false, customTopic = null, selectedImageURL = null, generatesWithAudio = false) => {

      let imageDescription = null;
      if (selectedImageURL) {
        imageDescription = await getImageAICaptionURL(selectedImageURL);
      }

      const basePrompt = customTopic 
        ? `Generate ${Math.ceil(quantity * 4)} short video ideas related to "${customTopic}" for ${company?.name}.`
        : `Generate ${Math.ceil(quantity * 4)} short video ideas for ${company?.name}.`;

      const response = await getByQueryChat(`
  ${basePrompt}

  WRITING INSTRUCTIONS:
  - Be very detailed and specific but keep it concise when possible
  - Include camera angles, lighting, and other details
  - Do not saturate the video with a lot of content; keep each idea focused and simple, but detailed
  - Do not include the company name
  - Do not use the COMPANY name
  - Do not include the COMPANY URL
  - Do not include the COMPANY logo
  - No captions
  - Do not use bullet points, only text
  - Do not explain the meaning of the video
  - Take into consideration that videos can't be longer than 10 seconds
  - Do not include hexadecimal colors in the video ideas
  ${customTopic ? `- Focus on the topic: "${customTopic}" but make it creative and engaging` : ''}
  ${generatesWithAudio 
    ? `- The video can include a speech or sound effects description or people talking or something audio related` 
      : '- The video MUST NOT include any references to audio or sound effects or people talking'
  }
  - Return a valid JSON STRING ARRAY with the video ideas
  ${imageDescription 
    ? `
    
      All video ideas must start with a frame of ${imageDescription}. Include it in your answer (at least a summary of the image), don't assume it's known.` 
    : ''}
  `, 
        false, 
        undefined, 
        undefined, 
        undefined, 
        undefined, 
        undefined, 
        undefined, 
        {
          "name": "video_advertisement_ideas",
          "schema": {
            "type": "object",
            "properties": {
              "video_ideas": {
                "type": "array",
                "description": "Array of video advertisement ideas",
                "items": {
                  "type": "string",
                  "description": "A creative video advertisement idea without company name, URL, or logo"
                }
              }
            },
            "required": ["video_ideas"],
            "additionalProperties": false
          },
          "strict": true
        }
      );

      if (response) {
        const video_ideas = aiJsonTextToArray(response);
        let suggestionsArray = video_ideas || [];
        suggestionsArray.reverse();
        suggestionsArray.length = Math.min(quantity, suggestionsArray.length);
        return suggestionsArray;
      }

      return [];
    }

    return { generateRandomContexts, generateRandomAIImagePrompts, generateRandomAIVideoPrompts }
}

export default useRandomContexts;